import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Bot, Megaphone, Activity, Settings2, BarChart3, AlertTriangle, ShieldCheck, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { inr } from "@/lib/format";
import { PageHeader, Panel } from "@/components/common";
import { AgentStatus, AgentControls, ConnBadge } from "@/components/agents";

function Metric({ l, v, tone = "" }) {
  return (
    <div className="py-2 flex items-center justify-between border-b last:border-0 text-sm">
      <span className="text-muted-foreground">{l}</span>
      <span className={`num font-semibold ${tone}`}>{v ?? <span className="text-muted-foreground font-normal text-xs">no data</span>}</span>
    </div>
  );
}

function AgentCard({ icon: Icon, a, metrics, onRun, act, base, prefix }) {
  return (
    <div className="rounded-xl border bg-card shadow-sm overflow-hidden" data-testid={`${prefix}-agent-card`}>
      <div className="bg-slate-950 text-white p-5 grain flex items-start gap-4">
        <div className="h-11 w-11 rounded-lg bg-amber-500 text-slate-950 grid place-items-center shrink-0"><Icon className="h-5 w-5" /></div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-amber-400">{prefix === "marketing" ? "Agent 1" : "Agent 2"}</div>
          <h3 className="font-heading text-xl font-bold">{a.name}</h3>
          <div className="text-xs text-slate-400 mt-1">
            Last run: {a.last_run ? `${new Date(a.last_run).toLocaleString("en-IN")} · ${a.last_run_status}` : "never"}
            {a.errors_24h > 0 && <span className="text-rose-400 ml-2">· {a.errors_24h} error(s) in 24h</span>}
          </div>
        </div>
        <AgentStatus agent={a} testid={`${prefix}-status`} />
      </div>
      <div className="p-5">
        <div className="mb-4">{metrics}</div>
        <div className="flex flex-wrap gap-2 items-center">
          <AgentControls agent={a} act={act} onRun={onRun} prefix={prefix} />
          <Button asChild variant="ghost" size="sm"><Link to={`${base}?tab=activity`} data-testid={`${prefix}-view-activity`}><Activity className="h-4 w-4 mr-1" /> Activity</Link></Button>
          <Button asChild variant="ghost" size="sm"><Link to={`${base}?tab=settings`} data-testid={`${prefix}-view-settings`}><Settings2 className="h-4 w-4 mr-1" /> Settings</Link></Button>
          <Button asChild variant="ghost" size="sm"><Link to={`${base}?tab=report`} data-testid={`${prefix}-view-reports`}><BarChart3 className="h-4 w-4 mr-1" /> Reports</Link></Button>
        </div>
      </div>
    </div>
  );
}

export default function AIControlCenter() {
  const [d, setD] = useState(null);
  const load = () => api.get("/agents-overview").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!d || !(d.marketing.running || d.social.running)) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [d]);
  if (!d) return <div className="text-sm text-muted-foreground">Loading control center…</div>;

  const act = (key) => async (a) => { try { await api.post(`/agents/${key}/${a}`); toast.success(a === "enable" ? "Agent enabled" : "Agent paused"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const run = (path, body) => async () => { try { const r = await api.post(path, body); toast.success(r.data.message); load(); } catch (e) { toast.error(errMsg(e)); } };
  const m = d.marketing.stats, s = d.social.stats;
  const integ = [
    ["AI writing (Claude Sonnet 5.5)", d.integrations.ai_text.state],
    ["AI web research (Gemini + Google Search)", d.integrations.ai_search.state],
    ["AI posters (Nano Banana)", d.integrations.ai_image.state],
    ["Email sending", d.integrations.email.state, d.integrations.email.note],
    ["Facebook Page", d.integrations.meta.facebook.state, d.integrations.meta.facebook.missing?.join(", ") || d.integrations.meta.facebook.message || d.integrations.meta.facebook.name],
    ["Instagram Business", d.integrations.meta.instagram.state, d.integrations.meta.instagram.missing?.join(", ") || d.integrations.meta.instagram.message || d.integrations.meta.instagram.name],
    ["Meta Ads account", d.integrations.meta.ads.state, d.integrations.meta.ads.missing?.join(", ") || d.integrations.meta.ads.message || d.integrations.meta.ads.name],
    ["Google Ads", d.integrations.google_ads.state, d.integrations.google_ads.missing.join(", ")],
  ];

  return (
    <div data-testid="ai-control-center">
      <PageHeader eyebrow="AI Agents" title="AI Business Development Center" subtitle="Monitor and control both growth agents. Every automated action is logged; outreach, publishing and ad spend need your approval."
        actions={<Button variant="outline" onClick={load} data-testid="refresh-overview-btn"><RefreshCw className="h-4 w-4 mr-1.5" /> Refresh</Button>} />
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
        <AgentCard icon={Bot} a={d.marketing} prefix="marketing" base="/ai/marketing" act={act("marketing")} onRun={run("/marketing/research", {})}
          metrics={<>
            <Metric l="New leads today (Sales Hunter)" v={m.new_leads} />
            <Metric l="High-priority opportunities" v={m.high_priority} tone="text-emerald-600" />
            <Metric l="Emails awaiting approval" v={m.emails_awaiting_approval} tone={m.emails_awaiting_approval ? "text-amber-600" : ""} />
            <Metric l="Follow-ups due" v={m.follow_ups_due} tone={m.follow_ups_due ? "text-rose-600" : ""} />
            <Metric l="Vendor registrations" v={m.vendor_registrations} />
            <Metric l="Deals won" v={m.deals_won} tone="text-emerald-600" />
          </>} />
        <AgentCard icon={Megaphone} a={d.social} prefix="social" base="/ai/social" act={act("social")} onRun={run("/social/plan", { days: 14, language: "English" })}
          metrics={<>
            <Metric l="Next scheduled post" v={s.next_scheduled ? `${s.next_scheduled.title.slice(0, 28)} · ${s.next_scheduled.at.replace("T", " ")}` : "none"} />
            <Metric l="Posts awaiting approval" v={s.awaiting_approval} tone={s.awaiting_approval ? "text-amber-600" : ""} />
            <Metric l="Generated creatives" v={s.creatives_generated} />
            <Metric l="Published posts" v={s.published_total} />
            <Metric l="Active campaigns" v={s.active_campaigns} />
            <Metric l="Ad spend today (Meta)" v={s.spend_today == null ? null : inr(s.spend_today)} />
            <Metric l="Ad spend lifetime (Meta)" v={s.spend_lifetime == null ? null : inr(s.spend_lifetime)} />
            <Metric l="Leads recorded" v={s.leads_recorded} />
          </>} />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-[1.3fr_1fr] gap-6 mb-6">
        <Panel title="AI Business Growth Advisor" action={<Button asChild size="sm" variant="outline"><Link to="/ai/advisor" data-testid="advisor-open-btn">Open advisor</Link></Button>} testid="advisor-agent-card">
          <div className="text-sm space-y-1">
            <div>Status: <AgentStatus agent={d.advisor} testid="advisor-card-status" /></div>
            <div className="text-muted-foreground">Last daily plan: {d.advisor.stats.last_plan_date || "never"}</div>
            <div className="text-muted-foreground">Tasks awaiting approval: {Object.values(d.advisor.stats.tasks).reduce((a, t) => a + t.pending_approval, 0)}</div>
          </div>
        </Panel>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-[1.3fr_1fr] gap-6">
        <Panel title="Integration Status" testid="integration-status">
          <div className="divide-y">
            {integ.map(([l, st, note]) => (
              <div key={l} className="py-2.5 flex items-center gap-3 text-sm" data-testid={`integration-${l.split(" ")[0].toLowerCase()}`}>
                <div className="min-w-0 flex-1"><div className="font-medium">{l}</div>{note && <div className="text-xs text-muted-foreground break-words">{st === "not_configured" ? `Needs: ${note}` : note}</div>}</div>
                <ConnBadge state={st} />
              </div>
            ))}
          </div>
        </Panel>
        <Panel title="Warnings & Safety Policy" testid="warnings-panel">
          {s.warnings.length > 0 && <div className="space-y-2 mb-4">{s.warnings.map((w, i) => <div key={i} className="flex gap-2 text-sm text-rose-600"><AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />{w}</div>)}</div>}
          <ul className="space-y-2 text-sm text-muted-foreground">
            {["Outreach emails: drafted only — you approve and send", "Posts: published only after your approval (auto-publish off)", "Meta campaigns: created PAUSED, activated only by you", "Budgets: hard ceiling enforced, never raised by AI", "No fabricated leads, results or vehicle availability"].map((t) => (
              <li key={t} className="flex gap-2"><ShieldCheck className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />{t}</li>
            ))}
          </ul>
        </Panel>
      </div>
    </div>
  );
}
